import { cn } from '@zealot/ui/lib/utils';
import { cva } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import type * as React from 'react';

const DOT_COUNT = 8;
const DOT_CYCLE_SECONDS = 1;

const DOTS = Array.from({ length: DOT_COUNT }, (_, index) => ({
  rotate: `${index * (360 / DOT_COUNT)}deg`,
  delay: `${-(((DOT_COUNT - index) % DOT_COUNT) / DOT_COUNT) * DOT_CYCLE_SECONDS}s`,
}));

const spinnerVariants = cva('inline-block shrink-0', {
  variants: {
    variant: {
      ring: 'size-4 animate-spin',
      dots: 'relative size-10',
    },
  },
  defaultVariants: { variant: 'ring' },
});

type RingSpinnerProps = { variant?: 'ring' } & React.ComponentProps<'svg'>;
type DotsSpinnerProps = { variant: 'dots' } & React.ComponentProps<'span'>;
type SpinnerProps = RingSpinnerProps | DotsSpinnerProps;

const DotsSpinner = ({ className, ...props }: Omit<DotsSpinnerProps, 'variant'>) => {
  return (
    <span
      data-slot="spinner"
      data-variant="dots"
      role="status"
      aria-label="Loading"
      className={cn(spinnerVariants({ variant: 'dots' }), className)}
      {...props}
    >
      {DOTS.map(({ rotate, delay }) => (
        <span key={rotate} aria-hidden className="absolute inset-0 flex items-center" style={{ rotate }}>
          <span
            className="size-1/5 animate-spinner-dot-pulse rounded-full bg-current opacity-50"
            style={{ animationDelay: delay }}
          />
        </span>
      ))}
    </span>
  );
};

const Spinner = (props: SpinnerProps) => {
  if (props.variant === 'dots') {
    const { variant: _variant, ...dotsProps } = props;
    return <DotsSpinner {...dotsProps} />;
  }

  const { variant: _variant, className, ...ringProps } = props;
  return (
    <Loader2Icon
      data-slot="spinner"
      data-variant="ring"
      role="status"
      aria-label="Loading"
      className={cn(spinnerVariants({ variant: 'ring' }), className)}
      {...ringProps}
    />
  );
};

export type { SpinnerProps };
export { Spinner, spinnerVariants };
